import React, { useState, useEffect } from 'react';
import {
  Search, Filter, ChevronLeft, ChevronRight, Eye, ShieldOff,
  ShieldCheck, MoreHorizontal, UserPlus, Download, RefreshCw,
  Shield, AlertTriangle, CheckCircle2, X, Trash2
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchAdminUsers,
  updateUserStatus,
  createAdminUser,
  deleteAdminUser,
  downloadCSV,
  convertUsersToCSV
} from '../utils/api';

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  plan_type: string;
  trust_score: number;
  status: 'active' | 'suspended' | 'banned';
  matches_count: number;
  reports_count: number;
  location_city: string;
  created_at: string;
}

const PLAN_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  chat_week: { label: 'Elite Weekly (₦5k)', bg: 'rgba(229,57,53,0.12)', color: '#ef4444' },
  chat_2hr: { label: '2-Hour Chat (₦500)', bg: 'rgba(139,92,246,0.12)', color: '#a78bfa' },
  match: { label: 'Instant Match (₦500)', bg: 'rgba(59,130,246,0.12)', color: '#60a5fa' },
  free: { label: 'Free Tier', bg: 'rgba(113,113,122,0.15)', color: '#a1a1aa' },
};

const STATUS_BADGE: Record<string, string> = {
  active: 'badge-green',
  suspended: 'badge-amber',
  banned: 'badge-red',
};

const AvatarCircle: React.FC<{ name: string }> = ({ name }) => {
  const initials = name
    .split(' ')
    .map(p => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AM';
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 shadow-sm"
      style={{ background: 'linear-gradient(135deg,#e53935,#8b5cf6)' }}
    >
      {initials}
    </div>
  );
};

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataMode, setDataMode] = useState<'test' | 'live'>('live');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // New user form state
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('+234 ');
  const [newUserPlan, setNewUserPlan] = useState('chat_week');
  const [newUserCity, setNewUserCity] = useState('Lagos');
  const [newUserTrust, setNewUserTrust] = useState(85);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const loadUsers = async (notify = false) => {
    setLoading(true);
    try {
      const data = await fetchAdminUsers({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search.trim() || undefined,
        plan: planFilter !== 'all' ? planFilter : undefined,
      });
      if (data && data.users) {
        setUsers(data.users);
      }
      if (data && data.mode) {
        setDataMode(data.mode);
      }
      if (notify) {
        showToast('✅ User directory refreshed');
      }
    } catch (e) {
      console.error('Error fetching users:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = searchParams.get('search');
    if (q !== null) {
      setSearch(q);
    }
  }, [searchParams]);

  useEffect(() => {
    loadUsers();

    const handleModeChanged = () => {
      loadUsers();
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, [statusFilter, planFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadUsers();
  };

  const handleExportCSV = () => {
    if (users.length === 0) {
      showToast('⚠️ No users to export');
      return;
    }
    const csv = convertUsersToCSV(users);
    downloadCSV(`amoura-users-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    showToast(`✅ Exported ${users.length} user records to CSV`);
  };

  const [submittingUser, setSubmittingUser] = useState(false);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserName.trim() || !newUserEmail.trim()) {
      showToast('⚠️ Please enter name and email');
      return;
    }
    setSubmittingUser(true);
    try {
      const res = await createAdminUser({
        name: newUserName.trim(),
        email: newUserEmail.trim(),
        phone: newUserPhone.trim() || `+234${Math.floor(1000000000 + Math.random() * 9000000000)}`,
        plan_type: newUserPlan,
        city: newUserCity,
        trust_score: newUserTrust,
      });

      if (res && res.error) {
        showToast(`❌ ${res.error}`);
      } else {
        setShowAddModal(false);
        setNewUserName('');
        setNewUserEmail('');
        showToast(`✅ User "${newUserName}" created in Cloudflare D1 database!`);
        await loadUsers(false);
      }
    } catch (err: any) {
      showToast(`❌ Error: ${err?.message || 'Failed to create user'}`);
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete user "${name}" from Cloudflare D1?`)) {
      return;
    }
    setActionInProgress(userId);
    try {
      await deleteAdminUser(userId);
      showToast(`✅ User "${name}" deleted from Cloudflare D1`);
      await loadUsers(false);
    } catch (err: any) {
      showToast(`❌ Failed to delete user: ${err?.message}`);
    } finally {
      setActionInProgress(null);
      setOpenMenuId(null);
    }
  };

  const handleAction = async (userId: string, action: 'suspend' | 'ban' | 'activate') => {
    setActionInProgress(userId);
    try {
      await updateUserStatus(userId, action);
      setUsers(prev => prev.map(u => {
        if (u.id !== userId) return u;
        return {
          ...u,
          status: action === 'activate' ? 'active' : action === 'suspend' ? 'suspended' : 'banned',
        };
      }));
      showToast(`User marked as ${action}d`);
    } catch (e) {
      console.error('Action failed:', e);
    } finally {
      setActionInProgress(null);
      setOpenMenuId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* ── Toast Notification ── */}
      {toastMessage && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#141428] border border-emerald-500/40 text-white shadow-2xl shadow-emerald-500/10">
          <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
          <p className="text-xs font-bold">{toastMessage}</p>
        </div>
      )}

      {/* ── Add User Modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0d0d1a] border border-[#1e1e3a] rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus size={18} className="text-pink-500" />
                <h3 className="text-sm font-bold text-white font-['Outfit']">Add New User / Persona</h3>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-3 pt-1">
              <div>
                <label className="block text-[11px] font-bold text-zinc-400 mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Chioma Adeyemi"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-zinc-400 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="e.g. chioma@amoura.app"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-zinc-400 mb-1">Phone (+234)</label>
                  <input
                    type="text"
                    value={newUserPhone}
                    onChange={(e) => setNewUserPhone(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white font-mono focus:outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-zinc-400 mb-1">City</label>
                  <input
                    type="text"
                    value={newUserCity}
                    onChange={(e) => setNewUserCity(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-zinc-400 mb-1">Initial Plan</label>
                  <select
                    value={newUserPlan}
                    onChange={(e) => setNewUserPlan(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                  >
                    <option value="chat_week">Elite Weekly (₦5k)</option>
                    <option value="chat_2hr">2-Hour Chat (₦500)</option>
                    <option value="match">Instant Match (₦500)</option>
                    <option value="free">Free Tier</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-zinc-400 mb-1">Trust Score (0-100)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newUserTrust}
                    onChange={(e) => setNewUserTrust(parseInt(e.target.value) || 50)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2 text-xs font-bold rounded-xl bg-zinc-800 text-zinc-300 hover:text-white cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingUser}
                  className="flex-1 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-lg shadow-pink-500/20 hover:from-red-500 hover:to-pink-500 cursor-pointer disabled:opacity-50"
                >
                  {submittingUser ? 'Creating in D1...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-white">
              User Management
            </h1>
            {dataMode === 'test' ? (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                🧪 Test Sandbox Personas
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                ⚡ Live D1 Customer DB
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400">
            {users.length} {dataMode === 'test' ? 'simulated sandbox' : 'registered'} profiles • {users.filter(u => u.status === 'active').length} active • {dataMode === 'test' ? 'Mock Mode' : 'Zero-Trust verified'}
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            onClick={() => loadUsers(true)}
            disabled={loading}
            className="btn-ghost flex items-center gap-2 text-sm cursor-pointer"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            className="btn-ghost flex items-center gap-2 text-sm cursor-pointer"
            title="Download users directory as CSV"
          >
            <Download size={14} /> Export CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2 text-sm cursor-pointer"
          >
            <UserPlus size={14} /> Add User
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            className="input pl-10 h-10 w-full"
            placeholder="Search by name, email, or phone (+234)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </form>

        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-zinc-400" />
            <select
              className="input h-10 w-auto pr-8 text-sm"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ cursor: 'pointer' }}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="suspended">Suspended Only</option>
              <option value="banned">Banned Only</option>
            </select>
          </div>

          <select
            className="input h-10 w-auto pr-8 text-sm"
            value={planFilter}
            onChange={e => setPlanFilter(e.target.value)}
            style={{ cursor: 'pointer' }}
          >
            <option value="all">All Plans</option>
            <option value="chat_week">Elite Weekly (₦5k)</option>
            <option value="chat_2hr">2-Hour Chat (₦500)</option>
            <option value="match">Instant Match (₦500)</option>
            <option value="free">Free Tier</option>
          </select>
        </div>
      </div>

      {/* Users Data Table */}
      <div className="glass rounded-2xl overflow-hidden border border-darkBorder shadow-xl">
        <div className="overflow-x-auto">
          <table className="data-table w-full text-left">
            <thead>
              <tr className="border-b border-darkBorder bg-zinc-900/60 text-xs font-bold uppercase tracking-wider text-zinc-400">
                <th className="py-3.5 px-4">User & Contact</th>
                <th className="py-3.5 px-4">Plan & Naira Tier</th>
                <th className="py-3.5 px-4">Trust Score</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Matches</th>
                <th className="py-3.5 px-4">Reports</th>
                <th className="py-3.5 px-4">Location</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-darkBorder">
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-zinc-400">
                    <RefreshCw className="animate-spin w-6 h-6 mx-auto mb-2 text-primaryRed" />
                    Connecting to Cloudflare D1 edge database...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-zinc-400">
                    No users match the selected query or filter.
                  </td>
                </tr>
              ) : (
                users.map(u => {
                  const planInfo = PLAN_LABELS[u.plan_type] || { label: u.plan_type || 'Free', bg: 'rgba(113,113,122,0.15)', color: '#a1a1aa' };
                  return (
                    <tr
                      key={u.id}
                      className="hover:bg-white/[0.02] transition-colors group"
                    >
                      {/* Name & Contact */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <AvatarCircle name={u.name} />
                          <div>
                            <p className="text-sm font-bold text-white group-hover:text-primaryRed transition-colors">
                              {u.name}
                            </p>
                            <p className="text-xs text-zinc-400 flex items-center gap-1.5">
                              <span>{u.phone}</span>
                              <span>•</span>
                              <span>{u.email}</span>
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Plan */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 cursor-pointer"
                      >
                        <span
                          className="text-xs font-semibold px-2.5 py-1 rounded-lg inline-block whitespace-nowrap"
                          style={{ background: planInfo.bg, color: planInfo.color }}
                        >
                          {planInfo.label}
                        </span>
                      </td>

                      {/* Trust Score */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 cursor-pointer"
                      >
                        <div className="flex items-center gap-1.5">
                          <Shield
                            size={14}
                            className={u.trust_score >= 80 ? 'text-green-400' : u.trust_score >= 60 ? 'text-blue-400' : 'text-amber-400'}
                          />
                          <span className="text-sm font-bold text-zinc-200">
                            {u.trust_score}
                          </span>
                          <span className="text-xs text-zinc-500">/100</span>
                        </div>
                      </td>

                      {/* Status */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 cursor-pointer"
                      >
                        <span className={`badge ${STATUS_BADGE[u.status] || 'badge-blue'} capitalize text-xs`}>
                          {u.status}
                        </span>
                      </td>

                      {/* Matches */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 font-semibold text-zinc-200 cursor-pointer"
                      >
                        {u.matches_count || 0}
                      </td>

                      {/* Reports */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 cursor-pointer"
                      >
                        {(u.reports_count || 0) > 0 ? (
                          <span className="badge badge-red flex items-center gap-1 w-fit text-xs font-semibold">
                            <AlertTriangle size={12} />
                            {u.reports_count} flag{u.reports_count > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-500">—</span>
                        )}
                      </td>

                      {/* Location */}
                      <td
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="py-3.5 px-4 text-xs text-zinc-400 cursor-pointer"
                      >
                        {u.location_city || 'Lagos'}, NG
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="relative inline-block text-left">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                            className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition"
                          >
                            <MoreHorizontal size={16} />
                          </button>

                          {openMenuId === u.id && (
                            <div
                              className="absolute right-0 z-20 w-44 rounded-xl shadow-2xl overflow-hidden mt-1 border border-darkBorder bg-zinc-900"
                            >
                              <button
                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-zinc-200 hover:bg-zinc-800 transition"
                                onClick={() => { navigate(`/users/${u.id}`); setOpenMenuId(null); }}
                              >
                                <Eye size={13} className="text-zinc-400" /> View Full Profile
                              </button>

                              {u.status !== 'suspended' && (
                                <button
                                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-amber-400 hover:bg-amber-500/10 transition"
                                  onClick={() => handleAction(u.id, 'suspend')}
                                  disabled={actionInProgress === u.id}
                                >
                                  <ShieldOff size={13} /> Suspend User
                                </button>
                              )}

                              {u.status !== 'active' && (
                                <button
                                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-green-400 hover:bg-green-500/10 transition"
                                  onClick={() => handleAction(u.id, 'activate')}
                                  disabled={actionInProgress === u.id}
                                >
                                  <ShieldCheck size={13} /> Activate User
                                </button>
                              )}

                              {u.status !== 'banned' && (
                                <button
                                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-red-400 hover:bg-red-500/10 transition"
                                  onClick={() => handleAction(u.id, 'ban')}
                                  disabled={actionInProgress === u.id}
                                >
                                  <ShieldOff size={13} /> Ban Account
                                </button>
                              )}

                              <button
                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-left text-rose-500 hover:bg-rose-500/10 transition border-t border-darkBorder"
                                onClick={() => handleDeleteUser(u.id, u.name)}
                                disabled={actionInProgress === u.id}
                              >
                                <Trash2 size={13} /> Delete Account
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer info */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-darkBorder gap-3">
          <p className="text-xs text-zinc-400">
            Displaying {users.length} active records from Cloudflare D1
          </p>
          <div className="flex gap-2">
            <button className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-1">
              <ChevronLeft size={13} /> Previous
            </button>
            <button className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-1">
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
